import { useCallback, useEffect, useState } from "react";
import type {
  BoardConnectionFilterIds,
  BoardConnectionFilterIdsUpdater,
  BoardSortBy,
  BoardSortOrder,
  BoardViewPreferencesViewModel,
  MetricKey,
  UseBoardViewPreferencesViewModelArgs,
} from "./BoardViewPreferences.ViewModel.types";

/**
 * Briefly written over `filter.showDoneTasks` while the switch was gone and its
 * value was turned into an Active Tasks filter. The switch is back, so the
 * stamp is read as what it replaced — done tasks hidden — and overwritten.
 */
const MIGRATED_SHOW_DONE = "migrated";

export const useBoardViewPreferencesViewModel = ({
  storage,
}: UseBoardViewPreferencesViewModelArgs = {}): BoardViewPreferencesViewModel => {
  const [showDoneTasks, setShowDoneTasks] = useState(true);
  const [selectedConnectionFilterIds, setSelectedConnectionFilterIdsState] =
    useState<BoardConnectionFilterIds>(null);
  const [searchTerm, setSearchTermState] = useState("");
  const [showAge, setShowAge] = useState(true);
  const [showDates, setShowDates] = useState(true);
  const [showConnectionTags, setShowConnectionTags] = useState(true);
  const [showEffort, setShowEffort] = useState(true);
  const [showOverview, setShowOverview] = useState(true);
  const [collapsedStatusIds, setCollapsedStatusIds] = useState<string[]>([]);
  const [sortBy, setSortByState] = useState<BoardSortBy>("none");
  const [sortOrder, setSortOrderState] = useState<BoardSortOrder>("desc");
  const [activeMetricFilters, setActiveMetricFilters] = useState<MetricKey[]>(
    [],
  );
  // Empty means no tag filtering at all, rather than "no tags selected" — the
  // same convention the metric filters use.
  const [selectedTagFilters, setSelectedTagFilters] = useState<string[]>([]);

  useEffect(() => {
    if (!storage) {
      return;
    }

    const doneSetting = storage.getItem("filter.showDoneTasks");
    const selectedConnectionFilterIdsSetting = storage.getItem(
      "filter.selectedConnectionFilterIds",
    );
    const ageSetting = storage.getItem("view.showAge");
    const datesSetting = storage.getItem("view.showDueDate");
    const tagsSetting = storage.getItem("view.showConnectionTags");
    const effortSetting = storage.getItem("view.showEffort");
    const overviewSetting = storage.getItem("view.showOverview");
    const sortBySetting = storage.getItem("view.sortBy") as BoardSortBy | null;
    const sortOrderSetting = storage.getItem(
      "view.sortOrder",
    ) as BoardSortOrder | null;
    const collapsedSetting = storage.getItem("view.collapsedStatusIds");

    if (doneSetting === MIGRATED_SHOW_DONE) {
      // Stamped by the release that removed the switch, which only ever stamped
      // a stored "false". Put that back, so anyone caught by it gets the board
      // they chose rather than one with done tasks suddenly in it.
      setShowDoneTasks(false);
      storage.setItem("filter.showDoneTasks", "false");
    } else if (doneSetting !== null) {
      setShowDoneTasks(doneSetting === "true");
    }

    if (selectedConnectionFilterIdsSetting !== null) {
      try {
        const parsed = JSON.parse(
          selectedConnectionFilterIdsSetting,
        ) as unknown;
        if (Array.isArray(parsed)) {
          setSelectedConnectionFilterIdsState(
            parsed.filter(
              (value): value is string => typeof value === "string",
            ),
          );
        }
      } catch {
        setSelectedConnectionFilterIdsState(null);
      }
    }

    if (ageSetting !== null) {
      setShowAge(ageSetting === "true");
    }

    if (datesSetting !== null) {
      setShowDates(datesSetting === "true");
    }

    if (tagsSetting !== null) {
      setShowConnectionTags(tagsSetting === "true");
    }

    if (effortSetting !== null) {
      setShowEffort(effortSetting === "true");
    }

    if (overviewSetting !== null) {
      setShowOverview(overviewSetting === "true");
    }

    if (sortBySetting !== null) {
      setSortByState(sortBySetting);
    }

    if (sortOrderSetting !== null) {
      setSortOrderState(sortOrderSetting);
    }

    // Collapsed columns are stored per board, so switching boards re-runs this
    // effect. They reset when the new board has nothing stored — otherwise the
    // previous board's collapsed columns would carry over.
    if (collapsedSetting === null) {
      setCollapsedStatusIds([]);
      return;
    }

    try {
      const parsed = JSON.parse(collapsedSetting) as unknown;
      setCollapsedStatusIds(
        Array.isArray(parsed)
          ? parsed.filter((value): value is string => typeof value === "string")
          : [],
      );
    } catch {
      setCollapsedStatusIds([]);
    }
  }, [storage]);

  const toggleShowDoneTasks = useCallback(() => {
    setShowDoneTasks((prev) => {
      const next = !prev;
      if (storage) {
        storage.setItem("filter.showDoneTasks", String(next));
      }
      return next;
    });
  }, [storage]);

  const setSelectedConnectionFilterIds = useCallback(
    (updater: BoardConnectionFilterIdsUpdater) => {
      setSelectedConnectionFilterIdsState((previous) => {
        const nextValue =
          typeof updater === "function" ? updater(previous) : updater;

        if (storage) {
          storage.setItem(
            "filter.selectedConnectionFilterIds",
            JSON.stringify(nextValue ?? []),
          );
        }

        return nextValue;
      });
    },
    [storage],
  );

  const setSearchTerm = useCallback((term: string) => {
    setSearchTermState(term);
  }, []);

  const toggleShowAge = useCallback(() => {
    setShowAge((prev) => {
      const next = !prev;
      if (storage) {
        storage.setItem("view.showAge", String(next));
      }
      return next;
    });
  }, [storage]);

  const toggleShowDates = useCallback(() => {
    setShowDates((prev) => {
      const next = !prev;
      if (storage) {
        storage.setItem("view.showDueDate", String(next));
      }
      return next;
    });
  }, [storage]);

  const toggleShowConnectionTags = useCallback(() => {
    setShowConnectionTags((prev) => {
      const next = !prev;
      if (storage) {
        storage.setItem("view.showConnectionTags", String(next));
      }
      return next;
    });
  }, [storage]);

  const toggleShowEffort = useCallback(() => {
    setShowEffort((prev) => {
      const next = !prev;
      if (storage) {
        storage.setItem("view.showEffort", String(next));
      }
      return next;
    });
  }, [storage]);

  const toggleShowOverview = useCallback(() => {
    setShowOverview((prev) => {
      const next = !prev;
      if (storage) {
        storage.setItem("view.showOverview", String(next));
      }
      return next;
    });
  }, [storage]);

  const setSortBy = useCallback(
    (newSortBy: BoardSortBy) => {
      setSortByState(newSortBy);
      if (storage) {
        storage.setItem("view.sortBy", newSortBy);
      }
    },
    [storage],
  );

  const setSortOrder = useCallback(
    (order: BoardSortOrder) => {
      setSortOrderState(order);
      if (storage) {
        storage.setItem("view.sortOrder", order);
      }
    },
    [storage],
  );

  const toggleColumnCollapsed = useCallback(
    (statusId: string) => {
      setCollapsedStatusIds((prev) => {
        const next = prev.includes(statusId)
          ? prev.filter((id) => id !== statusId)
          : [...prev, statusId];

        if (storage) {
          storage.setItem("view.collapsedStatusIds", JSON.stringify(next));
        }

        return next;
      });
    },
    [storage],
  );

  const expandAllColumns = useCallback(() => {
    setCollapsedStatusIds([]);
    if (storage) {
      storage.setItem("view.collapsedStatusIds", JSON.stringify([]));
    }
  }, [storage]);

  const toggleMetricFilter = useCallback((key: MetricKey) => {
    setActiveMetricFilters((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  }, []);

  const clearMetricFilters = useCallback(() => {
    setActiveMetricFilters([]);
  }, []);

  const toggleTagFilter = useCallback((tag: string) => {
    setSelectedTagFilters((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  }, []);

  const clearTagFilters = useCallback(() => {
    setSelectedTagFilters([]);
  }, []);

  return {
    showDoneTasks,
    selectedConnectionFilterIds,
    searchTerm,
    showAge,
    showDates,
    showConnectionTags,
    showEffort,
    showOverview,
    sortBy,
    sortOrder,
    collapsedStatusIds,
    toggleShowDoneTasks,
    setSelectedConnectionFilterIds,
    setSearchTerm,
    toggleShowAge,
    toggleShowDates,
    toggleShowConnectionTags,
    toggleShowEffort,
    toggleShowOverview,
    setSortBy,
    setSortOrder,
    toggleColumnCollapsed,
    expandAllColumns,
    activeMetricFilters,
    toggleMetricFilter,
    clearMetricFilters,
    selectedTagFilters,
    toggleTagFilter,
    clearTagFilters,
  };
};
